'use client';

import { useState, useRef, useEffect } from 'react';

import { TileMenu } from '@paction-2.0/ui';

import styles from './tileActionMenu.module.scss';

export type TileActionMenuItem = {
  /** Unique key for the menu item */
  key: string;
  /** Display label */
  label: string;
  /** Called when item is clicked */
  onClick: () => void;
  /** Whether this is a destructive action (shown in red) */
  destructive?: boolean;
  /** Whether the item is disabled */
  disabled?: boolean;
};

export type TileActionMenuProps = {
  /** Menu items to display */
  items: TileActionMenuItem[];
  /** Whether the entire menu is disabled */
  disabled?: boolean;
  /** Accessible label for the menu button */
  ariaLabel?: string;
};

/**
 * TileActionMenu - Dropdown menu for tile actions (Edit, Delete, etc.)
 *
 * Renders a three-dot vertical icon button that opens a dropdown menu.
 * Commonly used in tile grids for item-level actions.
 *
 * @example
 * ```tsx
 * <TileActionMenu
 *   items={[
 *     { key: 'edit', label: 'Edit', onClick: () => handleEdit(item) },
 *     { key: 'delete', label: 'Delete', onClick: () => handleDelete(item), destructive: true }
 *   ]}
 * />
 * ```
 */
export function TileActionMenu({
  items,
  disabled = false,
  ariaLabel = 'More actions'
}: TileActionMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleItemClick =
    (item: TileActionMenuItem) => (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!item.disabled) {
        item.onClick();
        setIsOpen(false);
      }
    };

  return (
    <TileMenu>
      <div ref={menuRef} className={styles.menuContainer}>
        <button
          type="button"
          className={styles.menuButton}
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-expanded={isOpen}
          aria-haspopup="menu"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <circle cx="12" cy="5" r="2" />
            <circle cx="12" cy="12" r="2" />
            <circle cx="12" cy="19" r="2" />
          </svg>
        </button>

        {isOpen && (
          <div className={styles.menuDropdown} role="menu">
            {items.map((item) => (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                className={`${styles.menuItem} ${item.destructive ? styles.menuItemDestructive : ''}`}
                onClick={handleItemClick(item)}
                disabled={item.disabled}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </TileMenu>
  );
}
